import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { User } from '@/types';
import {
  listNavigationContexts,
  navigationContextLandingHref,
  readRememberedNavigationContext,
  rememberNavigationContext,
  resolveActiveNavigationContext,
  type NavigationContextOption,
} from '@/lib/navigation/navigationContexts';

export function useActiveNavigationContext(
  user: User | null | undefined,
  workspaceId?: string | null,
) {
  const location = useLocation();
  const navigate = useNavigate();
  const [rememberedKey, setRememberedKey] = useState<string | null>(() => (
    user?.id ? readRememberedNavigationContext(user.id) : null
  ));

  useEffect(() => {
    setRememberedKey(user?.id ? readRememberedNavigationContext(user.id) : null);
  }, [user?.id]);

  useEffect(() => {
    const handleChange = (event: Event) => {
      const detail = (event as CustomEvent<{ userId?: string; key?: string }>).detail;
      if (detail?.userId === user?.id && detail.key) setRememberedKey(detail.key);
    };
    window.addEventListener('ax1:navigation-context-changed', handleChange);
    return () => window.removeEventListener('ax1:navigation-context-changed', handleChange);
  }, [user?.id]);

  const contexts = useMemo(() => listNavigationContexts(user), [user]);
  const activeContext = useMemo(() => resolveActiveNavigationContext(
    user,
    location.pathname,
    location.search,
    rememberedKey,
  ), [location.pathname, location.search, rememberedKey, user]);

  const selectContext = useCallback((context: NavigationContextOption) => {
    if (!user?.id) return;
    rememberNavigationContext(user.id, context.key);
    setRememberedKey(context.key);
    navigate(navigationContextLandingHref(context, {
      workspaceId,
      currentPath: location.pathname,
    }));
  }, [location.pathname, navigate, user?.id, workspaceId]);

  return { contexts, activeContext, selectContext };
}
